import React from "react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-5xl mx-auto p-6 text-gray-800 leading-relaxed">
      <SEO
        title="Privacy Policy | InvestorLanes"
        description="Read the InvestorLanes Privacy Policy to learn how we collect, use, store, and protect your information under POPIA and regional laws."
        canonicalUrl="https://investorlanes.com"
      />

      <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
      <p className="mb-6 text-sm text-gray-500"><strong>Last Updated:</strong> August 2026</p>

      <p className="mb-6">
        InvestorLanes ("we", "our", or "us") is a platform dedicated to connecting founders and investors across Africa. 
        Our operations are headquartered in <strong>Midrand, Johannesburg, South Africa</strong>. 
        As an African-first platform, we are strictly committed to safeguarding your personal data in accordance with the 
        <strong> Protection of Personal Information Act, No. 4 of 2013 (POPIA)</strong> of South Africa, as well as applicable regional data privacy 
        frameworks across the African continent (including the NDPR in Nigeria and Kenya's Data Protection Act).
      </p>

      <p className="mb-6">
        This Privacy Policy outlines how we collect, process, secure, and store your personal information when you create an 
        account, utilize our matching tools, or engage with our platform services.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">1. Information We Collect</h2>
      <p className="mb-4">To effectively match startups and investors, we collect and process the following categories of information:</p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li><strong>Account Registration Data:</strong> Full name, verified email address, secure encrypted passwords, country, and location.</li>
        <li><strong>Professional Profile Data:</strong> Investor or Founder operational credentials, business descriptions, financial summaries, pitch decks, investment metrics, and preferences.</li>
        <li><strong>Platform Communication Data:</strong> Messages, deal-flow inquiries, and correspondence history exchanged securely through our platform messaging infrastructure.</li>
        <li><strong>Technical System Data:</strong> IP addresses, browser specifications, access timestamps, unique cookie tokens, and telemetry data.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-3">2. Third-Party Payment Processing</h2>
      <p className="mb-6">
        InvestorLanes offers premium subscriptions and paywall-protected features. All financial processing is safely redirected to and executed by 
        <strong> Payment agencies </strong>, our designated secure third-party payment gateway. InvestorLanes <strong>never captures, views, or stores</strong> your 
        credit/debit card numbers, CVV details, bank account PINs, or online banking login credentials. Payment agencies processes all data in an encrypted environment under rigid 
        PCI-DSS compliance. 
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">3. Purpose and Legal Basis for Processing</h2>
      <p className="mb-4">In accordance with POPIA conditions for lawful processing, we handle your information based on contractual necessity, legitimate business interests, or your explicit consent to:</p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Authenticate user access and securely administer your account.</li>
        <li>Facilitate visibility, searches, and matches between active African entrepreneurs and capital providers.</li>
        <li>Verify operational premium accounts and communicate billing tokens with our payment partners.</li>
        <li>Protect our network infrastructure from algorithmic abuse, digital security threats, and fraudulent profile generation.</li>
        <li>Render operational platform updates, customer help desk resolutions, and platform-wide technical announcements.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-3">4. Cookies and Advertising Identifiers</h2>
      <p className="mb-6">
        We use operational cookies to track active login sessions and optimize user dashboard preferences. Our platform also integrates 
        Google AdSense. Google uses tracking cookies to serve targeted advertisements based on your visits to InvestorLanes and other websites across the internet. 
        You retain the right to manage your ad targeting variables or disable tracking completely via Google's Ad Settings and your browser preferences.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">5. Cross-Border Data Transfers</h2>
      <p className="mb-6">
        Because InvestorLanes operates on a pan-African scale, the information we collect may be transferred to, stored in, and processed within countries outside of South Africa. 
        We ensure that all cross-border transfers comply with Section 72 of POPIA. We only transfer data to jurisdictions that enforce equivalent data protection laws or 
        where strict standard contractual clauses bind our server hosts and sub-processors to equal data care guidelines.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">6. Data Retention and Safeguards</h2>
      <p className="mb-6">
        We implement enterprise-grade technical and organizational safeguards—including SSL/TLS transit encryption and isolated server parameters—to shield personal records from 
        unauthorized access, accidental leaks, or malicious alterations. We retain your information only for as long as your investor/founder account remains active or to 
        satisfy necessary legal, tax, and corporate accountability obligations in South Africa.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">7. Your Rights under POPIA</h2>
      <p className="mb-4">As a registered user, you possess explicit legal rights regarding your information. You may at any point request:</p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Confirmation of whether we hold any of your personal records.</li>
        <li>A formal copy of all data points stored within our databases.</li>
        <li>Immediate correction or updating of inaccurate, outdated, or incomplete profile items.</li>
        <li>The total destruction or deletion of your profile and data records from our active systems (Right to be Forgotten).</li>
      </ul>
      <p className="mb-6">
        To exercise any of these legal statutory rights, please send an explicit inquiry to our designated data compliance desk at 
        <a href="mailto:support@investorlanes.com" className="text-blue-600 hover:underline ml-1">support@investorlanes.com</a>. We will verify your identity and finalize your request within 30 days.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">8. Age Restrictions</h2>
      <p className="mb-6">
        InvestorLanes is exclusively engineered for business professionals, founders, and corporate entities. We do not intentionally log data from individuals under 18 years of age.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">9. Contact and Complaints</h2>
      <p className="mb-4">
        If you have questions about this policy, or if you believe InvestorLanes has not adequately resolved a data privacy issue, you have the right to lodge a complaint directly with the 
        South African Information Regulator. For direct correspondence with us, please use our legal contact channels:
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 inline-block min-w-[320px]">
        <p className="mb-1"><strong>Email:</strong> support@investorlanes.com</p>
        <p className="mb-1"><strong>Headquarters:</strong> Midrand, Johannesburg, South Africa</p>
        <p><strong>Digital Help Desk:</strong> https://investorlanes.com</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
